/**
 * MyViewsPage — landing page for EXTERNAL role users.
 *
 * Shows the list of external views the user has been granted access to.
 * Each card links to the public view page (/ext/{token}).
 *
 * planTEK Phase 10d.1 — EXTERNAL role support.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { api } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, LayoutDashboard } from 'lucide-react';
import { PortalInvoiceList } from '@/components/portal/PortalInvoiceList';

function useMyViews() {
  return useQuery({
    queryKey: ['external-views', 'my-views'],
    queryFn: () => api.externalViews.myViews(),
    staleTime: 5 * 60 * 1000,
  });
}

export function MyViewsPage() {
  const { user, signOut } = useAuth();
  const { data, isLoading, error } = useMyViews();

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header — no internal nav links */}
      <header className="border-b">
        <div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-primary" />
            <span className="font-semibold text-sm">Client Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <button
              onClick={() => signOut()}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] px-2"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">My Views</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Access the project views shared with you.
          </p>
        </div>

        {isLoading && (
          <div className="grid gap-4 sm:grid-cols-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Failed to load your views. Please try refreshing.
            </p>
          </div>
        )}

        {!isLoading && !error && data?.views?.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              No views have been shared with you yet. Contact your project manager if you
              believe this is an error.
            </p>
          </div>
        )}

        {!isLoading && !error && data?.views && data.views.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {data.views.map((view: any) => (
              <ViewCard key={view.id} view={view} />
            ))}
          </div>
        )}

        {/* planTEK Wave 1: Invoice payment panel (AC4/AC5) */}
        <div className="mt-8">
          <PortalInvoiceList />
        </div>
      </main>
    </div>
  );
}

function ViewCard({ view }: { view: any }) {
  const href = `/ext/${view.token}`;

  return (
    <Link
      to={href}
      className="group flex flex-col gap-2 rounded-lg border bg-card p-4 shadow-sm transition-shadow hover:shadow-md min-h-[44px]"
    >
      <div className="flex items-start justify-between">
        <h2 className="font-medium text-sm leading-tight group-hover:text-primary transition-colors">
          {view.name}
        </h2>
        <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      </div>

      {view.branding?.company_name && (
        <p className="text-xs text-muted-foreground">{view.branding.company_name}</p>
      )}

      {view.expires_at && (
        <p className="text-xs text-muted-foreground">
          Expires {new Date(view.expires_at).toLocaleDateString()}
        </p>
      )}
    </Link>
  );
}
